import { apiClient } from './api-client';

export interface Customer {
  id: string;
  tenantId: string;
  email: string;
  name?: string;
  phone?: string;
  avatarUrl?: string;
  organizationId?: string;
  status: 'active' | 'inactive' | 'blocked' | 'merged';
  /** Set while a linked CRM (HuHoKa) is the master for this record; its synced fields are read-only here. */
  managedBy?: string | null;
  /** BFF-supplied deep link to the record in that CRM (detail reads only). */
  externalEditUrl?: string | null;
  externalUrl?: string | null;
  externalUrlLabel?: string | null;
  timezone?: string;
  locale?: string;
  tags: string[];
  customFields: Record<string, any>;
  externalId?: string;
  source?: string;
  lastSeenAt?: string;
  createdAt: string;
  updatedAt: string;
  /** Record metadata. For a merged-away record it carries `mergedInto` (the
   *  surviving primary's id), `mergedAt`, and `mergeLogId`. */
  metadata?: Record<string, any>;
  organization?: {
    id: string;
    name: string;
    domain?: string;
    size?: string;
    metadata?: Record<string, any>;
    customFields?: Record<string, any>;
  };
  /** Every ticket that exists for this customer, any status. Only present
   *  when the request sets `withEngagement: true` — the BFF has to pay a
   *  second call to ticket-service for it, so other callers of getCustomers
   *  don't ask for it. */
  lifetimeTickets?: number;
}

export interface CustomerFilter {
  search?: string;
  status?: string;
  organizationId?: string;
  tag?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  withEngagement?: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CreateCustomerDto {
  email: string;
  name?: string;
  phone?: string;
  organizationId?: string;
  timezone?: string;
  locale?: string;
  externalId?: string;
  source?: string;
  tags?: string[];
  customFields?: Record<string, any>;
}

export interface UpdateCustomerDto {
  email?: string;
  name?: string;
  phone?: string;
  organizationId?: string;
  status?: string;
  timezone?: string;
  locale?: string;
  externalId?: string;
  source?: string;
  tags?: string[];
  customFields?: Record<string, any>;
}

export interface TimelineItem {
  id: string;
  type: string;
  category: 'activity' | 'note';
  description: string | null;
  ticketId?: string | null;
  actorId?: string | null;
  actorName?: string | null;
  actorType?: string;
  metadata: Record<string, any>;
  occurredAt: string;
  createdAt: string;
}

export interface SegmentCondition {
  field: string;
  operator: 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'starts_with' | 'ends_with' | 'greater_than' | 'less_than' | 'greater_than_or_equals' | 'less_than_or_equals' | 'is_empty' | 'is_not_empty' | 'in' | 'not_in' | 'between' | 'days_ago' | 'days_from_now';
  value: any;
}

export interface SegmentRule {
  id: string;
  conditions: SegmentCondition[];
  logicalOperator: 'AND' | 'OR';
}

export interface CustomerSegment {
  id: string;
  name: string;
  description?: string;
  type: 'dynamic' | 'static';
  rules?: SegmentRule[];
  customerCount: number;
  isActive: boolean;
  color?: string;
  icon?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface ImportJob {
  jobId: string;
  status: string;
  fileName: string;
  totalRows: number;
  processedRows: number;
  successCount: number;
  errorCount: number;
  errors: any[];
  createdAt: string;
  completedAt?: string;
}

export interface MergePreview {
  targetCustomer: Customer;
  sourceCustomer: Customer;
  contactsToMove: number;
  notesToMove: number;
  activitiesToMove: number;
  tagsToMerge: string[];
}

// ---------------------------------------------------------------------------
// Segment rule shape adapters (builder UI <-> customer-service backend)
//
// The backend stores and executes rules as groups of
//   { logic: 'AND'|'OR', rules: [{ field, operator, value }] }
// using a fixed operator vocabulary, while the builder UI works in
//   { id, conditions: [{ field, operator, value }], logicalOperator }.
// These helpers translate between the two so the backend's recalculation
// engine receives the shape it expects, and the edit form gets the shape it
// renders. Only the segment methods below use them — nothing else is affected.
// ---------------------------------------------------------------------------
const UI_TO_BACKEND_OPERATOR: Record<string, string> = {
  is_empty: 'is_not_set',
  is_not_empty: 'is_set',
};
const BACKEND_TO_UI_OPERATOR: Record<string, string> = {
  is_not_set: 'is_empty',
  is_set: 'is_not_empty',
};

function toBackendRules(rules?: SegmentRule[]): any[] | undefined {
  if (!rules) return undefined;
  return rules.map((group) => ({
    logic: group.logicalOperator || 'AND',
    rules: (group.conditions || [])
      .filter((c) => c.field) // drop unfinished blank rows the builder seeds
      .map((c) => ({
        field: c.field,
        operator: UI_TO_BACKEND_OPERATOR[c.operator] ?? c.operator,
        value: c.value,
      })),
  }));
}

function fromBackendRules(rules?: any[]): SegmentRule[] | undefined {
  if (!rules) return undefined;
  return rules.map((group) => ({
    id: crypto.randomUUID(),
    logicalOperator: (group.logic || group.logicalOperator || 'AND') as 'AND' | 'OR',
    conditions: ((group.rules || group.conditions || []) as any[]).map((c) => ({
      field: c.field,
      operator: (BACKEND_TO_UI_OPERATOR[c.operator] ?? c.operator) as SegmentCondition['operator'],
      value: c.value,
    })),
  }));
}

function adaptSegmentFromBackend(seg: CustomerSegment): CustomerSegment {
  return { ...seg, rules: fromBackendRules(seg.rules as unknown as any[]) };
}

export const customerService = {
  // Customer CRUD
  async getCustomers(filter?: CustomerFilter): Promise<PaginatedResponse<Customer>> {
    const params = new URLSearchParams();
    if (filter) {
      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, String(value));
        }
      });
    }
    const response = await apiClient.get(`/customers?${params.toString()}`);
    return response.data;
  },

  async getCustomer(id: string): Promise<Customer> {
    const response = await apiClient.get(`/customers/${id}`);
    return response.data;
  },

  async createCustomer(dto: CreateCustomerDto): Promise<Customer> {
    const response = await apiClient.post('/customers', dto);
    return response.data;
  },

  async updateCustomer(id: string, dto: UpdateCustomerDto): Promise<Customer> {
    const response = await apiClient.patch(`/customers/${id}`, dto);
    return response.data;
  },

  async deleteCustomer(id: string): Promise<void> {
    await apiClient.delete(`/customers/${id}`);
  },

  async getStatusCounts(): Promise<Record<string, number>> {
    const response = await apiClient.get('/customers/stats/status-counts');
    return response.data;
  },


  // Timeline
  async getTimeline(
    customerId: string,
    options?: { limit?: number; offset?: number; types?: string[] }
  ): Promise<{ items: TimelineItem[]; total: number }> {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', String(options.limit));
    if (options?.offset) params.append('offset', String(options.offset));
    if (options?.types) options.types.forEach((t) => params.append('types', t));
    const response = await apiClient.get(`/customers/${customerId}/timeline?${params.toString()}`);
    return response.data;
  },

  async addActivity(
    customerId: string,
    data: { type: string; description?: string; metadata?: Record<string, any> }
  ): Promise<{ success: boolean }> {
    const response = await apiClient.post(`/customers/${customerId}/activities`, data);
    return response.data;
  },

  async addNote(
    customerId: string,
    content: string,
    options?: { authorId?: string; isPinned?: boolean }
  ): Promise<{ success: boolean; id: string }> {
    const response = await apiClient.post(`/customers/${customerId}/notes`, {
      content,
      ...options,
    });
    return response.data;
  },

  // Merge operations
  async previewMerge(targetId: string, sourceId: string): Promise<MergePreview> {
    const response = await apiClient.get(
      `/customers/merge/preview?targetCustomerId=${targetId}&sourceCustomerId=${sourceId}`
    );
    return response.data;
  },

  async mergeCustomers(targetId: string, sourceId: string, note?: string): Promise<any> {
    const response = await apiClient.post('/customers/merge', {
      targetCustomerId: targetId,
      sourceCustomerId: sourceId,
      mergeNote: note,
    });
    return response.data;
  },

  async findDuplicates(): Promise<any> {
    const response = await apiClient.get('/customers/merge/duplicates');
    return response.data;
  },

  // Segments
  async getSegments(filter?: { isActive?: boolean; type?: string }): Promise<CustomerSegment[]> {
    const params = new URLSearchParams();
    if (filter?.isActive !== undefined) params.append('isActive', String(filter.isActive));
    if (filter?.type) params.append('type', filter.type);
    const response = await apiClient.get(`/segments?${params.toString()}`);
    return (response.data as CustomerSegment[]).map(adaptSegmentFromBackend);
  },

  async getSegment(id: string): Promise<CustomerSegment> {
    const response = await apiClient.get(`/segments/${id}`);
    return adaptSegmentFromBackend(response.data);
  },

  async createSegment(dto: {
    name: string;
    description?: string;
    type?: 'dynamic' | 'static';
    rules?: SegmentRule[];
    color?: string;
    icon?: string;
  }): Promise<CustomerSegment> {
    const { rules, ...rest } = dto;
    const payload = rules ? { ...rest, rules: toBackendRules(rules) } : rest;
    const response = await apiClient.post('/segments', payload);
    return adaptSegmentFromBackend(response.data);
  },

  async updateSegment(id: string, dto: {
    name?: string;
    description?: string;
    type?: 'dynamic' | 'static';
    rules?: SegmentRule[];
    color?: string;
    icon?: string;
    isActive?: boolean;
  }): Promise<CustomerSegment> {
    // Send only fields the backend UpdateSegmentDto accepts. `type` is
    // intentionally omitted — it's immutable after creation and the backend
    // rejects it (forbidNonWhitelisted → 400 "property type should not exist").
    const payload: Record<string, unknown> = {};
    if (dto.name !== undefined) payload.name = dto.name;
    if (dto.description !== undefined) payload.description = dto.description;
    if (dto.color !== undefined) payload.color = dto.color;
    if (dto.icon !== undefined) payload.icon = dto.icon;
    if (dto.isActive !== undefined) payload.isActive = dto.isActive;
    if (dto.rules !== undefined) payload.rules = toBackendRules(dto.rules);
    // BFF exposes PATCH /segments/:id (not PUT) — must match or the request 404s.
    const response = await apiClient.patch(`/segments/${id}`, payload);
    return adaptSegmentFromBackend(response.data);
  },

  async deleteSegment(id: string): Promise<void> {
    await apiClient.delete(`/segments/${id}`);
  },

  // Live "how many customers match" preview — no persistence. Sends only the
  // rules (the count is rule-driven); omitting `type` keeps the payload minimal
  // and avoids any backend whitelist mismatch.
  async previewSegment(rules: SegmentRule[]): Promise<number> {
    const response = await apiClient.post('/segments/preview', {
      rules: toBackendRules(rules),
    });
    return response.data?.count ?? 0;
  },

  async addCustomersToSegment(segmentId: string, customerIds: string[]): Promise<{ added: number }> {
    const response = await apiClient.post(`/segments/${segmentId}/members`, { customerIds });
    return response.data;
  },

  async removeCustomersFromSegment(segmentId: string, customerIds: string[]): Promise<{ removed: number }> {
    const response = await apiClient.delete(`/segments/${segmentId}/members`, { data: { customerIds } });
    return response.data;
  },

  async getSegmentMembers(
    segmentId: string,
    options?: { limit?: number; offset?: number }
  ): Promise<{ customers: Customer[]; total: number }> {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', String(options.limit));
    if (options?.offset) params.append('offset', String(options.offset));
    const response = await apiClient.get(`/segments/${segmentId}/members?${params.toString()}`);
    return response.data;
  },

  async getCustomerSegments(customerId: string): Promise<CustomerSegment[]> {
    const response = await apiClient.get(`/segments/customer/${customerId}`);
    return response.data;
  },

  // Import/Export
  async previewImport(dto: {
    fileContent: string;
    format: 'csv' | 'json';
    previewRows?: number;
  }): Promise<{
    headers: string[];
    sampleRows: Record<string, string>[];
    totalRows: number;
    suggestedMappings: any[];
    duplicates?: { row: number; email?: string; phone?: string; reasons: string[] }[];
    duplicateCount?: number;
  }> {
    const response = await apiClient.post('/import-export/import/preview', dto);
    return response.data;
  },

  async startImport(dto: {
    fileName: string;
    fileContent: string;
    format: 'csv' | 'json';
    mappings: any[];
    updateExisting?: boolean;
    skipErrors?: boolean;
    tags?: string[];
  }): Promise<ImportJob> {
    const response = await apiClient.post('/import-export/import', dto);
    return response.data;
  },

  async getImportJobs(filter?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ jobs: ImportJob[]; total: number }> {
    const params = new URLSearchParams();
    if (filter?.status) params.append('status', filter.status);
    if (filter?.limit) params.append('limit', String(filter.limit));
    if (filter?.offset) params.append('offset', String(filter.offset));
    const response = await apiClient.get(`/import-export/import/jobs?${params.toString()}`);
    return response.data;
  },

  async getImportJob(jobId: string): Promise<ImportJob> {
    const response = await apiClient.get(`/import-export/import/jobs/${jobId}`);
    return response.data;
  },

  async exportCustomers(dto: {
    format?: 'csv' | 'json' | 'xlsx';
    fields?: string[];
    filters?: {
      status?: string;
      search?: string;
      tags?: string[];
      segmentId?: string;
    };
    includeCustomFields?: boolean;
  }): Promise<{ fileName: string; content: string; recordCount: number }> {
    const response = await apiClient.post('/import-export/export', dto);
    return response.data;
  },
};

// Organization types
export interface Organization {
  id: string;
  tenantId: string;
  name: string;
  domain?: string;
  industry?: string;
  size?: string;
  website?: string;
  phone?: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  };
  parentId?: string;
  tier?: string;
  tags: string[];
  status: 'active' | 'inactive';
  customerCount?: number;
  ticketCount?: number;
  openTicketCount?: number;
  children?: Organization[];
  /** Set while a linked CRM (HuHoKa) is the master for this record; its synced fields are read-only here. */
  managedBy?: string | null;
  externalId?: string | null;
  /** BFF-supplied deep link to the record in that CRM (detail reads only). */
  externalEditUrl?: string | null;
  customFields?: Record<string, any>;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface OrganizationFilter {
  search?: string;
  status?: string;
  industry?: string;
  tier?: string;
  size?: string;
  parentId?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface OrganizationStats {
  totalCustomers: number;
  activeCustomers: number;
  totalTickets: number;
  openTickets: number;
  avgResponseTime?: number;
  avgResolutionTime?: number;
  satisfactionScore?: number;
  slaComplianceRate?: number;
}

export interface OrganizationActivity {
  id: string;
  type: string;
  description: string;
  metadata?: Record<string, any>;
  performedBy?: {
    id: string;
    name: string;
  };
  occurredAt: string;
}

export const organizationService = {
  // Organization CRUD
  async getOrganizations(filter?: OrganizationFilter): Promise<PaginatedResponse<Organization>> {
    const params = new URLSearchParams();
    if (filter) {
      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, String(value));
        }
      });
    }
    const response = await apiClient.get(`/organizations?${params.toString()}`);
    return response.data;
  },

  async getOrganization(id: string): Promise<Organization> {
    const response = await apiClient.get(`/organizations/${id}`);
    return response.data;
  },

  async createOrganization(dto: Partial<Organization>): Promise<Organization> {
    const response = await apiClient.post('/organizations', dto);
    return response.data;
  },

  async updateOrganization(id: string, dto: Partial<Organization>): Promise<Organization> {
    const response = await apiClient.patch(`/organizations/${id}`, dto);
    return response.data;
  },

  async deleteOrganization(id: string): Promise<void> {
    await apiClient.delete(`/organizations/${id}`);
  },

  // Organization relationships
  async getOrganizationCustomers(
    orgId: string,
    options?: { page?: number; limit?: number; search?: string }
  ): Promise<PaginatedResponse<Customer>> {
    const params = new URLSearchParams();
    if (options?.page) params.append('page', String(options.page));
    if (options?.limit) params.append('limit', String(options.limit));
    if (options?.search) params.append('search', options.search);
    const response = await apiClient.get(`/organizations/${orgId}/customers?${params.toString()}`);
    return response.data;
  },

  async getOrganizationStats(orgId: string): Promise<OrganizationStats> {
    const response = await apiClient.get(`/organizations/${orgId}/stats`);
    return response.data;
  },

  async getOrganizationTickets(
    orgId: string,
    options?: { page?: number; limit?: number; status?: string }
  ): Promise<PaginatedResponse<any>> {
    const params = new URLSearchParams();
    if (options?.page) params.append('page', String(options.page));
    if (options?.limit) params.append('limit', String(options.limit));
    if (options?.status) params.append('status', options.status);
    const response = await apiClient.get(`/organizations/${orgId}/tickets?${params.toString()}`);
    return response.data;
  },

  async getOrganizationActivity(
    orgId: string,
    options?: { limit?: number; offset?: number }
  ): Promise<{ items: OrganizationActivity[]; total: number }> {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', String(options.limit));
    if (options?.offset) params.append('offset', String(options.offset));
    const response = await apiClient.get(`/organizations/${orgId}/activity?${params.toString()}`);
    return response.data;
  },

  async getChildOrganizations(parentId: string): Promise<Organization[]> {
    const response = await apiClient.get(`/organizations/${parentId}/children`);
    return response.data;
  },

  // Notes
  async addNote(orgId: string, note: string): Promise<void> {
    await apiClient.post(`/organizations/${orgId}/notes`, { note });
  },
};

export default customerService;
